import { useEffect, useId, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import discoverSuggestions from './discoverSuggestions';

/**
 * JobTitleTypeahead (TD-2105, Contact Enrichment Stream B; multi-add polish TD-2484)
 *
 * Dedicated lightweight creatable chip-typeahead for job titles. Purpose-built
 * rather than reusing MultiSelect, which is heavily DOM-hacked around
 * multiselect-react-dropdown and not creatable.
 *
 * - When `openOnFocus` (the default, used on desktop) the dropdown is open
 *   whenever the input is focused: with an empty query it lists the (unselected)
 *   suggestions so the field is browsable; while typing it filters them
 *   (case-insensitive substring).
 * - On mobile (`openOnFocus={false}`) a bare focus/tap does NOT open the list —
 *   the absolute-positioned list would cover the management-level pills rendered
 *   directly below it (TD-2528). It opens only once the user types and closes
 *   again when the query is cleared (e.g. after a pick), keeping seniorities visible.
 * - When the query matches no suggestion, a single "creatable" row shows the
 *   typed text so the user can click to add a free-text title (Enter also adds).
 * - Selecting a title (click, or ArrowUp/ArrowDown + Enter) adds a removable
 *   chip, clears the input and KEEPS the dropdown open so several titles can be
 *   added in a row from the same list. Backspace on an empty input removes the
 *   last chip.
 *
 * Implements the ARIA combobox (listbox popup) pattern: the input owns
 * `aria-controls`/`aria-activedescendant`, options carry stable ids and
 * `aria-selected`.
 *
 * Controlled: parent owns `value` (array of title strings), updated via
 * `onChange`. Job titles combine with management levels using AND logic.
 */

const MAX_SUGGESTIONS = 8;

const JobTitleTypeahead = ({
  value = [],
  onChange,
  suggestions = discoverSuggestions,
  placeholder = 'Search job titles',
  inputId,
  openOnFocus = true
}) => {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);
  const blurTimer = useRef(null);
  const reactId = useId();
  const listboxId = `${reactId}-listbox`;
  const optionId = (idx) => `${reactId}-option-${idx}`;

  // Dropdown contents as a single indexable list of `{ label, isCreate }`.
  // Suggestions not already chosen are offered — the full (capped) list when the
  // query is empty so the field is browsable, filtered by case-insensitive
  // substring while typing. When nothing matches, offer the typed text as a
  // creatable row (unless it is blank or already a chip).
  const options = useMemo(() => {
    const trimmed = query.trim();
    const q = trimmed.toLowerCase();
    const selectedLower = new Set(value.map((v) => v.toLowerCase()));
    const unselected = suggestions.filter((s) => !selectedLower.has(s.toLowerCase()));
    const matched = (q ? unselected.filter((s) => s.toLowerCase().includes(q)) : unselected).slice(
      0,
      MAX_SUGGESTIONS
    );
    if (matched.length) {
      return matched.map((label) => ({ label, isCreate: false }));
    }
    if (q && !selectedLower.has(q)) {
      return [{ label: trimmed, isCreate: true }];
    }
    return [];
  }, [query, suggestions, value]);

  // Desktop opens on focus; mobile (openOnFocus=false) only once the user types,
  // so the absolute list never covers the management-level pills below (TD-2528).
  const showDropdown = focused && options.length > 0 && (openOnFocus || query.trim().length > 0);

  // Keep the active option in range as the option list changes.
  useEffect(() => {
    setActiveIndex((prev) => (prev >= options.length ? options.length - 1 : prev));
  }, [options.length]);

  // Clear any pending blur timer on unmount.
  useEffect(() => () => clearTimeout(blurTimer.current), []);

  const addTag = (rawTitle) => {
    const title = rawTitle.trim();
    if (!title) return;
    const exists = value.some((v) => v.toLowerCase() === title.toLowerCase());
    if (!exists) {
      onChange([...value, title]);
    }
    // Clear the query but leave the dropdown open (the input keeps focus) so the
    // user can add several titles in a row from the default list (TD-2484).
    setQuery('');
    setActiveIndex(-1);
  };

  // Select an option and return focus to the input (shared by click + the
  // option's keyboard mirror).
  const selectOption = (title) => {
    addTag(title);
    inputRef.current?.focus();
  };

  const removeTag = (title) => {
    onChange(value.filter((v) => v !== title));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown' && options.length) {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % options.length);
    } else if (e.key === 'ArrowUp' && options.length) {
      e.preventDefault();
      setActiveIndex((prev) => (prev <= 0 ? options.length - 1 : prev - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && options[activeIndex]) {
        addTag(options[activeIndex].label);
      } else {
        addTag(query);
      }
    } else if (e.key === 'Backspace' && !query && value.length) {
      removeTag(value.at(-1));
    }
  };

  return (
    <div className="job-title-typeahead">
      <div className="job-title-typeahead-control">
        {value.map((title) => (
          <span key={title} className="job-title-typeahead-chip">
            <span className="job-title-typeahead-chip-label">{title}</span>
            <button
              type="button"
              className="job-title-typeahead-chip-remove"
              aria-label={`Remove ${title}`}
              onClick={() => removeTag(title)}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          className="job-title-typeahead-input"
          role="combobox"
          aria-expanded={showDropdown}
          aria-autocomplete="list"
          aria-controls={showDropdown ? listboxId : undefined}
          aria-activedescendant={
            showDropdown && activeIndex >= 0 ? optionId(activeIndex) : undefined
          }
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIndex(-1);
          }}
          onFocus={() => setFocused(true)}
          // Delay blur so a suggestion mousedown registers before the list unmounts.
          onBlur={() => {
            blurTimer.current = setTimeout(() => setFocused(false), 120);
          }}
          onKeyDown={handleKeyDown}
        />
      </div>

      {showDropdown && (
        <ul className="job-title-typeahead-options" role="listbox" id={listboxId}>
          {options.map((opt, idx) => (
            <li
              key={opt.isCreate ? `create:${opt.label}` : opt.label}
              id={optionId(idx)}
              role="option"
              aria-selected={idx === activeIndex}
              className={`job-title-typeahead-option${idx === activeIndex ? ' active' : ''}${
                opt.isCreate ? ' job-title-typeahead-option-create' : ''
              }`}
              // preventDefault on mousedown keeps input focus (beats blur);
              // the actual selection happens on click.
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActiveIndex(idx)}
              onClick={() => selectOption(opt.label)}
              // Primary keyboard path is the input's ArrowUp/Down + Enter
              // (aria-activedescendant). This mirror keeps the option itself
              // operable for AT that focuses options directly, and satisfies
              // click-events-have-key-events.
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  selectOption(opt.label);
                }
              }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

JobTitleTypeahead.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  suggestions: PropTypes.arrayOf(PropTypes.string),
  placeholder: PropTypes.string,
  inputId: PropTypes.string,
  openOnFocus: PropTypes.bool
};

export default JobTitleTypeahead;
